import { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Printer, Boxes } from "lucide-react";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trpcClient } from "@/lib/trpc";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { formatMoney, formatNumber, cn, extractErrorMessage } from "@/lib/utils";
import { formatUnitQty, packingLabel } from "@apv-erp/status";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { ImportDialog } from "@/components/shared/ImportDialog";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { ConfigLabel } from "@/components/shared/ConfigLabel";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useMaterialTypeLookup } from "@/hooks/useFKLookup";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useImportRefs } from "@/hooks/useImportRefs";
import { findByCode, runUpsertImport } from "@/lib/import-kit";
import { emsTabClass } from "@/components/ui/ems-tabs";

/**
 * Cột Excel vật tư — một định nghĩa dùng chung cho xuất, file mẫu và nhập (04/11/2026): cùng tiêu đề, cùng thứ tự.
 * Tồn hiện tại chỉ xuất (importable: false): nhập Excel không đổi tồn — tồn chỉ đổi qua phiếu kho.
 */
const NUM_FMT = "#,##0.##";
const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "materialId", label: "Mã vật tư", required: true },
  { key: "name", label: "Tên vật tư", required: true },
  { key: "subType", label: "Nhóm vật tư", ref: "materialSubtype" },
  { key: "materialType.label", label: "Loại vật tư", ref: "materialType" },
  { key: "gsm", label: "Định lượng (GSM)", type: "decimal", min: 0, numFmt: NUM_FMT },
  { key: "thicknessMicron", label: "Độ dày (micron)", type: "decimal", min: 0, numFmt: NUM_FMT },
  { key: "width", label: "Rộng (mm)", type: "decimal", min: 0, numFmt: NUM_FMT },
  { key: "length", label: "Dài (mm)", type: "decimal", min: 0, numFmt: NUM_FMT },
  { key: "height", label: "Cao (mm)", type: "decimal", min: 0, numFmt: NUM_FMT },
  { key: "unit", label: "ĐVT chính", ref: "unit" },
  { key: "secondaryUnit", label: "ĐVT phụ", ref: "unit" },
  { key: "secondaryFactor", label: "Quy đổi (1 ĐVT phụ = ? ĐVT chính)", type: "decimal", min: 0, numFmt: "0.######" },
  { key: "packingNote", label: "Ghi chú đóng gói" },
  { key: "unitPrice", label: "Đơn giá", type: "decimal", min: 0, numFmt: NUM_FMT },
  { key: "minStock", label: "Tồn an toàn", type: "int", min: 0 },
  { key: "vendor.code", label: "Mã NCC", ref: "vendor" },
  { key: "specs", label: "Quy cách khác" },
  { key: "currentStock", label: "Tồn hiện tại", type: "decimal", numFmt: NUM_FMT, importable: false },
];

export default function MaterialListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  // Dynamic lookup for material categories/types
  const { options: materialTypeOptions } = useMaterialTypeLookup();
  const filterFields: FilterField[] = useMemo(() => [
    {
      key: "category",
      label: "Nhóm VT",
      type: "combobox-multi",
      options: materialTypeOptions.length > 0
        ? materialTypeOptions.map((opt) => ({
            value: (opt.original as any)?.code || opt.id,
            label: opt.label,
          }))
        : [
            { value: "PAPER", label: "Giấy" },
            { value: "INK", label: "Mực in" },
            { value: "FILM", label: "Màng" },
            { value: "GLUE", label: "Keo" },
            { value: "CHEMICAL", label: "Hóa chất" },
            { value: "OTHER", label: "Khác" },
          ],
    },
    { key: "materialId", label: "Mã VT", type: "text", placeholder: "VT-..." },
    { key: "name", label: "Tên VT", type: "text", placeholder: "Nhập tên..." },
    { key: "gsm", label: "GSM", type: "text", placeholder: "Định lượng..." },
    { key: "unit", label: "ĐVT", type: "text", placeholder: "Kg, m, tấm..." },
    {
      key: "vendor.shortName",
      label: "NCC",
      type: "text",
      placeholder: "Tên NCC...",
    },
    {
      key: "status",
      label: "Trạng thái",
      type: "combobox-multi",
      options: [
        { value: "ACTIVE", label: "Hoạt động" },
        { value: "INACTIVE", label: "Ngừng dùng" },
      ],
    },
  ], [materialTypeOptions]);

  const {
    paginatedItems,
    isLoading,
    error,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    groupFilter,
    onGroupChange,
    groupCounts,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "materials",
    queryFn: (params) => trpcClient.materials.list.query(params) as any,
    groupParamName: "typeId",
    groupCountsQueryFn: () => trpcClient.materials.groupCounts.query() as any,
    extraParams: {
      isDeleted: showDeleted || undefined,
    },
  });

  const rowIds = paginatedItems.map((m: any) => m.id);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectionMode, clearSelection, selectedCount } =
    rowSelection;

  if (error) console.error("[MaterialList] API error:", error);

  // Sidebar: group by materialType — use server counts
  const sidebarItems = useMemo(() => {
    return (groupCounts as any[])
      .map((t: any) => ({
        id: t.id,
        label:
          (t.label || "Chưa phân loại").length > 18
            ? (t.label || "Chưa phân loại").slice(0, 18) + "…"
            : t.label || "Chưa phân loại",
        count: t.count,
      }))
      .sort((a: any, b: any) =>
        a.id === "_untyped"
          ? 1
          : b.id === "_untyped"
            ? -1
            : a.label.localeCompare(b.label),
      );
  }, [groupCounts]);

  const handleDelete = async (id: string) => {
    try {
      await trpcClient.materials.delete.mutate({ id });
      toast.success("Đã xóa bản ghi thành công.");

      await queryClient.invalidateQueries({ queryKey: ["materials"] });
      await queryClient.invalidateQueries({ queryKey: ["materials-lookup"] });
    } catch (err: any) {
      toast.error(err?.message || "Xoá vật tư thất bại");
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await trpcClient.materials.restore.mutate({ id });
      await queryClient.invalidateQueries({ queryKey: ["materials"] });
      await queryClient.invalidateQueries({ queryKey: ["materials-lookup"] });
      toast.success("Đã phục hồi bản ghi thành công.");
    } catch (err: any) {
      toast.error(err?.message || "Phục hồi vật tư thất bại.");
    }
  };

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_vat_tu",
    sheetName: "Vật Tư",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.materials.list.query(params),
    currentFilters: serverParams,
    selectedIds,
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [
      {
        label: "Xuất Excel",
        icon: <Printer className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
    ];
    if (hasPermission("materials", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.materials.delete.mutate({ id }),
            actionName: "xoá",
            itemType: "vật tư",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["materials"] });
              queryClient.invalidateQueries({ queryKey: ["materials-lookup"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xoá ${selectedCount} vật tư đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  // Nhập Excel vật tư (04/11/2026): mã đã có → cập nhật, mã mới → tạo; không đổi tồn. Loại vật tư / Mã NCC / Nhóm / ĐVT
  // khớp danh mục ở ImportDialog (giá trị chưa có thì hỏi thêm mới) → ở đây đã là id / tên chuẩn.
  const importRefs = useImportRefs();
  const materialImportRefs = useMemo(
    () => ({
      materialSubtype: importRefs.materialSubtype,
      materialType: importRefs.materialType,
      unit: importRefs.unit,
      vendor: importRefs.vendor,
    }),
    [importRefs],
  );
  const handleImport = async (rows: Record<string, unknown>[]) => {
    const result = await runUpsertImport(rows, {
      codeOf: (row) => String(row.materialId ?? "").trim(),
      findExisting: findByCode(() => trpcClient.materials.list.query({ limit: 10000 } as any), (m) => m.materialId),
      prepare: (row, mode) => {
        const { "materialType.label": typeId, "vendor.code": vendorId, currentStock: _stock, materialId, ...rest } = row as any;
        const data: Record<string, unknown> = { ...rest };
        if (data.gsm !== null && data.gsm !== undefined) data.gsm = String(data.gsm);
        if (typeId !== undefined) data.typeId = typeId || null;
        if (vendorId !== undefined) data.vendorId = vendorId || null;
        if (mode === "create") data.materialId = materialId;
        return data;
      },
      create: (data) => trpcClient.materials.create.mutate(data as any),
      update: (id, data) => trpcClient.materials.update.mutate({ id, data } as any),
    });
    await queryClient.invalidateQueries({ queryKey: ["materials"] });
    await queryClient.invalidateQueries({ queryKey: ["materials-lookup"] });
    return result;
  };

  const COLS = 11;

  return (
    <>
      <ListPageShell
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowDeleted(false)}
              className={emsTabClass(!showDeleted, { size: "sm" })}
            >
              <Boxes className="w-3.5 h-3.5 text-indigo-600" />
              <span>Đang Sử Dụng</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  !showDeleted
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {!showDeleted ? totalItems : (groupCounts?.reduce((s: number, c: any) => s + (c.count || 0), 0) || totalItems)}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setShowDeleted(true)}
              className={emsTabClass(showDeleted, { size: "sm", tone: "rose" })}
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              <span>Thùng rác</span>
            </button>
          </div>
        }
        headerFilters={
          <div className="flex items-center gap-2 flex-wrap">
            <PageFilterTags
              items={sidebarItems.map((item) => ({
                key: item.id,
                label: item.label,
                count: item.count,
                color: item.id === "_untyped" ? ("slate" as const) : ("blue" as const),
              }))}
              selectedKeys={groupFilter ? [groupFilter] : []}
              onSelect={(key) =>
                onGroupChange(key === groupFilter ? null : key)
              }
              dropdownLabel="Loại vật tư"
            />
          </div>
        }
        unitLabel="VT"
        actions={
          <ExportImportButtons
            onExport={handleExport}
            exportLoading={isExporting}
            onImportClick={
              hasPermission("materials", "create")
                ? () => setShowImport(true)
                : undefined
            }
            onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "vat_tu")}
          >
            {hasPermission("materials", "create") && (
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => navigate({ to: "/materials/create" })}
              >
                <Plus size={14} className="mr-1" /> Thêm VT
              </Button>
            )}
          </ExportImportButtons>
        }
        filterFields={filterFields}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <table
          className={cn(
            "w-full text-xs caption-bottom apv-compact-table min-w-[1400px]",
            selectionMode && "apv-select-mode",
          )}
        >
          <thead className="apv-table-header">
            <tr>
              <DataTableSelectAllCheckbox rowSelection={rowSelection} />
              <SortableHeader
                label="Mã VT"
                field="materialId"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code w-[80px] whitespace-nowrap"
              />
              <SortableHeader
                label="Tên vật tư"
                field="name"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[220px] whitespace-nowrap"
              />
              <SortableHeader
                label="Loại VT"
                field="materialType.label"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[100px] whitespace-nowrap"
              />
              <SortableHeader
                label="NCC"
                field="vendor.shortName"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[180px] whitespace-nowrap"
              />
              <SortableHeader
                label="ĐVT"
                field="unit"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[60px] whitespace-nowrap"
              />
              <SortableHeader
                label="GSM"
                field="gsm"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[70px] whitespace-nowrap"
              />
              <SortableHeader
                label="Tồn kho"
                field="currentStock"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="text-right w-[80px] whitespace-nowrap"
              />
              <SortableHeader
                label="Tồn min"
                field="minStock"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="text-right w-[70px] whitespace-nowrap"
              />
              <SortableHeader
                label="Đơn giá (VNĐ)"
                field="unitPrice"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="text-right min-w-[100px] whitespace-nowrap"
              />
              <th className="text-right w-[70px] px-2 py-1.5 apv-col-actions whitespace-nowrap">TT</th>
            </tr>
            {showFilter && (
              <InlineFilterRow
                columns={[
                  CHECKBOX_COL_NULL, // checkbox
                  { key: "materialId" },
                  { key: "name" },
                  null, // loại VT
                  { key: "vendor.shortName" },
                  { key: "unit" },
                  { key: "gsm" },
                  null, // tồn kho
                  null, // tồn min
                  null, // đơn giá
                  null, // TT
                ]}
                filterFields={filterFields}
                filterValues={filterValues}
                onFilterChange={onFilterChange}
              />
            )}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: COLS }).map((_, j) => (
                    <td key={j} className="px-2 py-2">
                      <Skeleton className="h-3 w-12" />
                    </td>
                  ))}
                </tr>
              ))
            ) : paginatedItems.length === 0 ? (
              <tr>
                <td
                  colSpan={COLS}
                  className="text-center text-slate-400 py-12 text-sm"
                >
                  {searchQuery
                    ? "Không tìm thấy vật tư phù hợp"
                    : "Chưa có dữ liệu vật tư"}
                </td>
              </tr>
            ) : (
              paginatedItems.map((m: any) => (
                <DataTableRow
                  key={m.id}
                  id={m.id}
                  rowSelection={rowSelection}
                  className="hover:bg-blue-50/40 transition-colors border-b border-slate-100 last:border-0"
                  onClick={() =>
                    navigate({
                      to: "/materials/$materialId",
                      params: { materialId: m.id },
                    })
                  }
                >
                  <DataTableRowCheckbox id={m.id} rowSelection={rowSelection} />
                  <td className="apv-col-code px-2 py-1.5">
                    <span className="font-semibold text-indigo-600 font-mono">
                      {m.materialId}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 truncate max-w-[160px]">
                    <div className="font-medium text-slate-900 truncate">
                      {m.name}
                    </div>
                    {m.specs && (
                      <div className="text-[11px] text-slate-500 truncate">
                        {m.specs}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 truncate max-w-[100px]">
                    {m.materialType?.label || m.materialType?.code || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-700 truncate max-w-[100px]">
                    {m.vendor?.shortName ||
                      m.vendor?.name ||
                      m.vendorName ||
                      "—"}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap" title={m.secondaryUnit ? packingLabel(m) : undefined}>
                    <ConfigLabel
                      categoryCode="unit"
                      value={m.unit}
                      fallback="—"
                    />
                    {/* ĐVT phụ: "Tờ · Ram (500)" (04/11/2026) */}
                    {m.secondaryUnit && Number(m.secondaryFactor) > 0 && (
                      <span className="text-slate-500"> · {m.secondaryUnit} ({formatUnitQty(Number(m.secondaryFactor), 3)})</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-slate-600">
                    {m.gsm ? `${m.gsm} g/m²` : m.thicknessMicron != null ? `${Number(m.thicknessMicron)} mic` : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {formatNumber(m.currentStock)}
                    {m.minStock != null &&
                      m.currentStock != null &&
                      m.currentStock <= m.minStock && (
                        <span className="text-destructive ml-0.5">⚠</span>
                      )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-slate-500">
                    {formatNumber(m.minStock)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono font-medium">
                    {formatMoney(m.unitPrice)}
                  </td>
                  <td
                    className="px-2 py-1.5 text-right apv-col-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-0 apv-row-actions">
                      {m.isDeleted ? (
                        hasPermission("materials", "delete") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => handleRestore(m.id)}
                          >
                            Phục hồi
                          </Button>
                        )
                      ) : (
                        <>
                          {hasPermission("materials", "delete") && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                >
                                  <Trash2 size={12} className="text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Xác nhận xoá</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Bạn có chắc muốn xoá vật tư này? (Có thể phục hồi trong Thùng rác)
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Huỷ</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(m.id)}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    Xoá
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </DataTableRow>
              ))
            )}
          </tbody>
        </table>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        title="Import Vật Tư"
        columns={EXPORT_COLUMNS}
        onImport={handleImport}
        refs={materialImportRefs}
        onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "vat_tu")}
      />
    </>
  );
}
